import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2, Users, CalendarCheck, Mail, TrendingUp, ShieldCheck, Check, X,
  RotateCcw, Eye, Activity, UserCheck, CalendarPlus, MessageSquare, Gift,
  LayoutDashboard, MapPin, Clock, Sparkles, Box as BoxIcon, CalendarRange,
  FileText, ChevronDown, ChevronRight, Cpu, Ruler,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import AdminConfig from "./admin/AdminConfig";
import AdminTimeSlots from "./admin/AdminTimeSlots";
import AdminSpecialDates from "./admin/AdminSpecialDates";
import AdminBoxConfig from "./admin/AdminBoxConfig";
import AdminScheduler from "./admin/AdminScheduler";
import AdminParentalWaivers from "./admin/AdminParentalWaivers";
import AdminBlogs from "./admin/AdminBlogs";
import AdminMachines from "./admin/AdminMachines";
import AdminLanes from "./admin/AdminLanes";
import AdminUserMemberships from "./admin/AdminUserMemberships";
import AdminHome from "./admin/AdminHome";
import AdminSettings from "./admin/AdminSettings";
import AdminWaivers from "./admin/AdminWaivers";
import AdminUsers from "./admin/AdminUsers";
import AdminEmailTemplates from "./admin/AdminEmailTemplates";
import AdminWaiverSettings from "./admin/AdminWaiverSettings";

const statusClass = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-red-50 text-red-700 border-red-200",
};

// Sidebar structure inspired by the reference admin panel
const navGroups = [
  {
    items: [
      { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
      { id: "bookings", label: "Bookings", icon: CalendarCheck },
      { id: "activity", label: "Activity feed", icon: Activity },
    ],
  },
  {
    label: "Site CMS",
    items: [
      { id: "home-cms", label: "Home Content", icon: FileText },
      { id: "site-settings", label: "Settings", icon: Cpu },
      { id: "waiver-settings", label: "Waiver Text", icon: ShieldCheck },
    ],
  },
  {
    label: "Venues Management",
    items: [
      { id: "venues", label: "Venues", icon: MapPin },
      { id: "time-slots", label: "Time Slots", icon: Clock },
      { id: "special-dates", label: "Special Dates", icon: Sparkles },
      { id: "box-config", label: "Box Config", icon: BoxIcon },
      { id: "lanes", label: "Lanes", icon: Ruler },
      { id: "machines", label: "Machines", icon: Cpu },
    ],
  },
  {
    label: "Booking Management",
    items: [
      { id: "scheduler", label: "Scheduler Grid", icon: CalendarRange },
    ],
  },
  {
    label: "Membership & People",
    items: [
      { id: "users", label: "All Users", icon: Users },
      { id: "memberships", label: "Plans", icon: Gift },
      { id: "user-memberships", label: "Members", icon: ShieldCheck },
      { id: "coaches", label: "Coaches", icon: Users },
    ],
  },
  {
    label: "Query Management",
    items: [
      { id: "waivers", label: "Signed Waivers", icon: ShieldCheck },
      { id: "parental-waivers", label: "Parental Waivers", icon: ShieldCheck },
      { id: "leads", label: "Leads", icon: TrendingUp },
      { id: "contacts", label: "Contacts", icon: MessageSquare },
      { id: "emails", label: "Emails", icon: Mail },
      { id: "email-templates", label: "Email Templates", icon: Mail },
    ],
  },
  {
    items: [
      { id: "blogs", label: "Blogs", icon: FileText },
    ],
  },
];

const Admin = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [emails, setEmails] = useState([]);
  const [activity, setActivity] = useState([]);
  const [checkInsByBooking, setCheckInsByBooking] = useState({});
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [collapsed, setCollapsed] = useState({});

  const fetchOverview = useCallback(async () => {
    setFetching(true);
    try {
      const [s, b, l, c, e, a] = await Promise.all([
        api.get("/admin/stats", { withCredentials: true }),
        api.get(`/admin/bookings${statusFilter ? `?status=${statusFilter}` : ""}`, { withCredentials: true }),
        api.get("/admin/leads", { withCredentials: true }),
        api.get("/admin/contacts", { withCredentials: true }),
        api.get("/admin/emails", { withCredentials: true }),
        api.get("/admin/activity", { withCredentials: true }),
      ]);
      setStats(s.data); setBookings(b.data); setLeads(l.data);
      setContacts(c.data); setEmails(e.data); setActivity(a.data);
    } catch {
      toast.error("Could not load admin data.");
    } finally {
      setFetching(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login", { replace: true }); return; }
    if (!user.is_admin) { toast.error("Admin access only."); navigate("/account", { replace: true }); return; }
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user?.is_admin) return;
    if (["dashboard", "bookings", "activity", "leads", "contacts", "emails"].includes(section)) {
      fetchOverview();
    }
  }, [section, fetchOverview, user]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status }, { withCredentials: true });
      toast.success(`Booking ${status}.`);
      fetchOverview();
    } catch { toast.error("Update failed."); }
  };

  const toggleCheckIns = async (bookingId) => {
    if (expandedBooking === bookingId) { setExpandedBooking(null); return; }
    setExpandedBooking(bookingId);
    if (!checkInsByBooking[bookingId]) {
      try {
        const { data } = await api.get(`/admin/bookings/${bookingId}/checkins`, { withCredentials: true });
        setCheckInsByBooking((prev) => ({ ...prev, [bookingId]: data }));
      } catch { toast.error("Could not load check-ins."); }
    }
  };

  if (loading || !user || !user.is_admin) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-spike-blue" /></div>;
  }

  const statCards = stats ? [
    { label: "Bookings", value: stats.bookings_total, icon: CalendarCheck },
    { label: "Pending", value: stats.bookings_pending, icon: Loader2 },
    { label: "Confirmed", value: stats.bookings_confirmed, icon: Check },
    { label: "Check-ins", value: stats.checkins ?? 0, icon: Eye },
    { label: "Leads", value: stats.leads, icon: TrendingUp },
    { label: "Emails", value: stats.emails ?? 0, icon: Mail },
    { label: "Contacts", value: stats.contacts, icon: Mail },
    { label: "Members", value: stats.users, icon: Users },
  ] : [];

  const titleFor = navGroups.flatMap((g) => g.items).find((i) => i.id === section)?.label || "Dashboard";

  return (
    <div className="bg-slate-50 min-h-screen flex" data-testid="admin-page">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 min-h-screen sticky top-0 shrink-0 hidden lg:flex flex-col" data-testid="admin-sidebar">
        <div className="px-5 py-5 border-b border-slate-200">
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">Spike Sports</div>
          <div className="font-semibold text-slate-900 mt-0.5">Admin Console</div>
        </div>
        <nav className="flex-1 p-3 space-y-4 overflow-y-auto">
          {navGroups.map((group, gi) => {
            const isCollapsed = collapsed[gi];
            return (
              <div key={gi}>
                {group.label && (
                  <button
                    type="button"
                    data-testid={`sidebar-group-${gi}`}
                    onClick={() => setCollapsed((c) => ({ ...c, [gi]: !c[gi] }))}
                    className="w-full px-3 mb-1 flex items-center justify-between text-xs font-semibold text-slate-500 uppercase tracking-wider hover:text-slate-700"
                  >
                    {group.label}
                    {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </button>
                )}
                {!isCollapsed && (
                  <div className="space-y-0.5">
                    {group.items.map((it) => {
                      const Icon = it.icon;
                      const active = section === it.id;
                      return (
                        <button
                          key={it.id}
                          type="button"
                          data-testid={`sidebar-${it.id}`}
                          onClick={() => setSection(it.id)}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                            active
                              ? "bg-spike-blue-pale text-spike-blue font-medium"
                              : "text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          {it.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <div className="px-5 py-4 border-t border-slate-200 text-xs text-slate-500">
          Signed in as<br />
          <span className="font-medium text-slate-700">{user.email}</span>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Mobile section selector */}
        <div className="lg:hidden bg-white border-b border-slate-200 p-3">
          <select value={section} onChange={(e) => setSection(e.target.value)} className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="mobile-section-select">
            {navGroups.flatMap((g) => g.items).map((it) => (
              <option key={it.id} value={it.id}>{it.label}</option>
            ))}
          </select>
        </div>

        <div className="px-6 lg:px-10 py-8 max-w-[1400px]">
          <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900" data-testid="admin-section-title">{titleFor}</h1>
              <p className="text-sm text-slate-500 mt-0.5">Admin dashboard</p>
            </div>
            {["dashboard", "bookings", "activity", "leads", "contacts", "emails"].includes(section) && (
              <button type="button" data-testid="admin-refresh" onClick={fetchOverview} className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium px-3 py-2 rounded-md">
                <RotateCcw className="w-3.5 h-3.5" /> Refresh
              </button>
            )}
          </header>

          {/* ====== DASHBOARD ====== */}
          {section === "dashboard" && (
            <div className="space-y-6" data-testid="section-dashboard">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats">
                {statCards.map((s) => {
                  const Icon = s.icon;
                  return (
                    <div key={s.label} className="bg-white border border-slate-200 rounded-lg p-5 shadow-card">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="text-xs text-slate-500 font-medium uppercase tracking-wider">{s.label}</div>
                          <div className="mt-2 text-3xl font-bold text-slate-900">{s.value ?? "—"}</div>
                        </div>
                        <div className="w-9 h-9 rounded-lg bg-spike-blue-pale flex items-center justify-center">
                          <Icon className="w-4 h-4 text-spike-blue" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-card">
                <h3 className="font-semibold text-slate-900 mb-2">Quick links</h3>
                <p className="text-sm text-slate-600 mb-4">Jump straight to the sections you use most.</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    ["bookings", "View bookings"],
                    ["scheduler", "Scheduler grid"],
                    ["time-slots", "Time slots & pricing"],
                    ["parental-waivers", "Parental waivers"],
                    ["blogs", "Blog posts"],
                  ].map(([id, lbl]) => (
                    <button key={id} type="button" onClick={() => setSection(id)} className="text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded-md">
                      {lbl} →
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ====== BOOKINGS ====== */}
          {section === "bookings" && (
            <div className="space-y-4" data-testid="section-bookings">
              <div className="flex items-center gap-2">
                <select data-testid="admin-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-white border border-slate-300 rounded-md px-3 py-2 text-sm">
                  <option value="">All statuses</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              {fetching ? <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-spike-blue" /></div> : (
                <div className="bg-white border border-slate-200 rounded-lg shadow-card overflow-x-auto" data-testid="admin-bookings-table">
                  <table className="w-full min-w-[900px] text-sm">
                    <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-4 py-3">Ref</th>
                        <th className="text-left px-4 py-3">When</th>
                        <th className="text-left px-4 py-3">Customer</th>
                        <th className="text-left px-4 py-3">Sport / Setup</th>
                        <th className="text-left px-4 py-3">Status</th>
                        <th className="text-left px-4 py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {bookings.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">No bookings.</td></tr>}
                      {bookings.map((b) => (
                        <React.Fragment key={b.id}>
                          <tr data-testid={`admin-booking-row-${b.id}`} className="hover:bg-slate-50">
                            <td className="px-4 py-3 font-mono text-xs font-medium text-slate-900">#{b.id.slice(0, 6).toUpperCase()}</td>
                            <td className="px-4 py-3 text-slate-700">{b.date} · {b.time_slot}</td>
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-900">{b.full_name}</div>
                              <div className="text-xs text-slate-500">{b.email}</div>
                            </td>
                            <td className="px-4 py-3 text-slate-700 capitalize">
                              {b.sport} · {b.lane_type?.replace("_", " ")}{b.machine ? ` (${b.machine.replace("_", " ")})` : ""}
                              {b.lane_numbers?.length > 0 && <div className="text-xs text-slate-500">Lanes {b.lane_numbers.join(", ")}</div>}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${statusClass[b.status] || statusClass.pending}`}>
                                {b.status}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1 flex-wrap">
                                <button type="button" data-testid={`admin-view-checkins-${b.id}`} onClick={() => toggleCheckIns(b.id)} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50">
                                  <Eye className="w-3 h-3" /> Check-ins
                                </button>
                                {b.status !== "confirmed" && (
                                  <button type="button" data-testid={`admin-confirm-${b.id}`} onClick={() => updateStatus(b.id, "confirmed")} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200">
                                    <Check className="w-3 h-3" /> Confirm
                                  </button>
                                )}
                                {b.status !== "cancelled" && (
                                  <button type="button" data-testid={`admin-cancel-${b.id}`} onClick={() => updateStatus(b.id, "cancelled")} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-red-50 text-red-700 hover:bg-red-100 border border-red-200">
                                    <X className="w-3 h-3" /> Cancel
                                  </button>
                                )}
                                {b.status === "cancelled" && (
                                  <button type="button" onClick={() => updateStatus(b.id, "pending")} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50">Reopen</button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {expandedBooking === b.id && (
                            <tr data-testid={`admin-checkins-row-${b.id}`} className="bg-slate-50">
                              <td colSpan={6} className="p-4">
                                <div className="text-xs font-medium text-slate-600 mb-2 uppercase tracking-wider">Check-ins ({checkInsByBooking[b.id]?.length || 0})</div>
                                {!checkInsByBooking[b.id] ? <Loader2 className="w-4 h-4 animate-spin text-spike-blue" /> :
                                  checkInsByBooking[b.id].length === 0 ? <div className="text-sm text-slate-500">No one has checked in yet.</div> :
                                  <ul className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                    {checkInsByBooking[b.id].map((ci) => (
                                      <li key={ci.id} className="bg-white border border-slate-200 rounded p-3 text-sm">
                                        <div className="font-medium text-slate-900">{ci.user_name || ci.user_email}</div>
                                        <div className="text-xs text-slate-500">{ci.user_email}</div>
                                        <div className="text-xs text-spike-blue mt-1">{new Date(ci.checked_in_at).toLocaleString()}</div>
                                      </li>
                                    ))}
                                  </ul>
                                }
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ====== ACTIVITY ====== */}
          {section === "activity" && (
            <div data-testid="section-activity">
              {fetching ? <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-spike-blue" /></div> : (
                <div className="bg-white border border-slate-200 rounded-lg shadow-card overflow-hidden" data-testid="admin-activity-feed">
                  {activity.length === 0 ? (
                    <div className="p-12 text-center text-slate-500 text-sm">No activity yet.</div>
                  ) : (
                    <ol className="divide-y divide-slate-100">
                      {activity.map((evt, i) => {
                        const Icon = { checkin: UserCheck, booking: CalendarPlus, lead: Gift, contact: MessageSquare }[evt.type] || Activity;
                        const tone = {
                          checkin: "text-emerald-700 bg-emerald-50",
                          booking: "text-spike-blue bg-spike-blue-pale",
                          lead: "text-amber-700 bg-amber-50",
                          contact: "text-violet-700 bg-violet-50",
                        }[evt.type] || "text-slate-700 bg-slate-50";
                        const at = evt.at ? new Date(evt.at) : null;
                        return (
                          <li key={i} data-testid={`activity-row-${i}`} className="px-5 py-4 flex items-start gap-3 hover:bg-slate-50">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${tone}`}><Icon className="w-4 h-4" /></div>
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-baseline gap-2">
                                <span className="font-medium text-slate-900">{evt.actor_name || evt.actor_email}</span>
                                <span className="text-xs text-slate-400 uppercase tracking-wider">{evt.type}</span>
                                <span className="ml-auto text-xs text-slate-500">{at ? at.toLocaleString() : ""}</span>
                              </div>
                              <div className="text-sm text-slate-600 mt-0.5">{evt.summary}</div>
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ====== EMAILS ====== */}
          {section === "emails" && (
            <div data-testid="section-emails">
              <div className="bg-white border border-slate-200 rounded-lg shadow-card overflow-x-auto" data-testid="admin-emails-table">
                <table className="w-full min-w-[700px] text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                    <tr><th className="text-left px-4 py-3">Sent at</th><th className="text-left px-4 py-3">To</th><th className="text-left px-4 py-3">Subject</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Body</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {emails.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No emails yet.</td></tr>}
                    {emails.map((m) => (
                      <tr key={m.id} className="hover:bg-slate-50 align-top">
                        <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{new Date(m.sent_at).toLocaleString()}</td>
                        <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{m.to_email}</td>
                        <td className="px-4 py-3 font-medium text-slate-900">{m.subject}</td>
                        <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-amber-50 text-amber-700 border border-amber-200">{m.status}</span></td>
                        <td className="px-4 py-3 text-slate-600 whitespace-pre-line max-w-md text-xs">{m.body}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ====== LEADS ====== */}
          {section === "leads" && (
            <div className="bg-white border border-slate-200 rounded-lg shadow-card overflow-x-auto" data-testid="admin-leads-table">
              <table className="w-full min-w-[600px] text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                  <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Phone</th><th className="text-left px-4 py-3">Source</th><th className="text-left px-4 py-3">Code</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {leads.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">No leads yet.</td></tr>}
                  {leads.map((l) => (
                    <tr key={l.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-900">{l.name}</td>
                      <td className="px-4 py-3 text-slate-700">{l.email}</td>
                      <td className="px-4 py-3 text-slate-700">{l.phone || "—"}</td>
                      <td className="px-4 py-3 text-slate-700 capitalize">{l.source.replace("_", " ")}</td>
                      <td className="px-4 py-3 font-mono text-xs text-spike-blue">{l.code}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ====== CONTACTS ====== */}
          {section === "contacts" && (
            <div className="bg-white border border-slate-200 rounded-lg shadow-card overflow-x-auto" data-testid="admin-contacts-table">
              <table className="w-full min-w-[700px] text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                  <tr><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th><th className="text-left px-4 py-3">Phone</th><th className="text-left px-4 py-3">Message</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {contacts.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-slate-500">No messages yet.</td></tr>}
                  {contacts.map((c) => (
                    <tr key={c.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-900 whitespace-nowrap">{c.name}</td>
                      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{c.email}</td>
                      <td className="px-4 py-3 text-slate-700 whitespace-nowrap">{c.phone || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{c.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Sub-pages */}
          {section === "venues" && <AdminConfig />}
          {section === "memberships" && <AdminConfig />}
          {section === "coaches" && <AdminConfig />}
          {section === "time-slots" && <AdminTimeSlots />}
          {section === "special-dates" && <AdminSpecialDates />}
          {section === "box-config" && <AdminBoxConfig />}
          {section === "lanes" && <AdminLanes />}
          {section === "machines" && <AdminMachines />}
          {section === "user-memberships" && <AdminUserMemberships />}
          {section === "scheduler" && <AdminScheduler />}
          {section === "parental-waivers" && <AdminParentalWaivers />}
          {section === "blogs" && <AdminBlogs />}
          {section === "home-cms" && <AdminHome />}
          {section === "site-settings" && <AdminSettings />}
          {section === "waivers" && <AdminWaivers />}
          {section === "users" && <AdminUsers />}
          {section === "email-templates" && <AdminEmailTemplates />}
          {section === "waiver-settings" && <AdminWaiverSettings />}
        </div>
      </main>
    </div>
  );
};

export default Admin;
